export type ReviewDecision = 'approved' | 'rejected';

export type ReviewContentSignalCode =
  | 'external_link'
  | 'promotional_language'
  | 'contact_solicitation'
  | 'unsafe_markup'
  | 'credential_solicitation'
  | 'obfuscated_text';

export interface ReviewContentCheck {
  method: 'heuristic-v1' | null;
  signals: {
    category: 'advertising' | 'malicious';
    code: ReviewContentSignalCode;
  }[];
  status: 'clear' | 'flagged' | 'unavailable';
}

export interface PendingReview {
  id: string;
  body: string;
  book: {
    id: string;
    title: string;
    authors: string[];
    coverImageUrl: string | null;
    isbn10: string | null;
    isbn13: string | null;
    language: string | null;
    pageCount: number | null;
    publishedYear: number | null;
    publisher: string | null;
  };
  contentCheck: ReviewContentCheck;
  containsSpoilers: boolean;
  rating: number | null;
  reviewerId: string | null;
  reviewerName: string | null;
  source: 'living_library' | 'goodreads';
  submittedAt: string;
  version: number;
}

export interface PendingReviewQueue {
  nextCursor: string | null;
  total: number;
  reviews: PendingReview[];
}

export interface PendingQuote {
  id: string;
  body: string;
  book: PendingReview['book'];
  contentCheck: ReviewContentCheck;
  language: string;
  pageNumber: number | null;
  readerId: string;
  readerName: string | null;
  submittedAt: string;
  version: number;
}

export interface PendingQuoteQueue {
  nextCursor: string | null;
  quotes: PendingQuote[];
  total: number;
}

export type ReviewCheckStatus = ReviewContentCheck['status'];

export interface AdminReviewFilters {
  checkStatus: ReviewCheckStatus | null;
  reviewerIds: string[];
}

export interface PendingReviewerOption {
  id: string;
  name: string | null;
  pendingReviewCount: number;
}

export type RatingRefreshHealthStatus = 'critical' | 'healthy' | 'never_run' | 'running';
export type RatingRefreshRunStatus = 'failed' | 'running' | 'succeeded';

export interface RatingRefreshRun {
  candidates: { authors: number; works: number };
  completedAt: string | null;
  durationMs: number | null;
  errorCode: string | null;
  heartbeatAt: string;
  id: string;
  result: {
    authors: { deferred: number; refreshed: number; unresolved: number };
    works: { deferred: number; refreshed: number; unresolved: number };
  };
  source: 'cloud_run_job' | 'operator';
  startedAt: string;
  status: RatingRefreshRunStatus;
}

export interface RatingCoverage {
  cached: number;
  due: number;
  mapped: number;
  rated: number;
  total: number;
  unresolved: number;
}

export interface OpenLibraryRatingOperations {
  coverage: { authors: RatingCoverage; works: RatingCoverage };
  health: {
    reason:
      | 'healthy'
      | 'latest_attempt_failed'
      | 'never_succeeded'
      | 'refresh_running'
      | 'refresh_stuck'
      | 'success_stale';
    status: RatingRefreshHealthStatus;
  };
  lastAttempt: RatingRefreshRun | null;
  lastSuccess: RatingRefreshRun | null;
  provider: 'open_library';
  recentRuns: RatingRefreshRun[];
  schedule: {
    cron: string;
    staleAfterDays: number;
    stuckAfterMinutes: number;
    timeZone: string;
  };
}

export type CataloguePipelineHealthStatus = 'critical' | 'degraded' | 'healthy';
export type CataloguePipelineIssueCode =
  | 'backfill_latest_failed'
  | 'backfill_stuck'
  | 'enrichment_failed_jobs'
  | 'enrichment_lease_expired'
  | 'enrichment_overdue'
  | 'provider_unavailable'
  | 'quota_exhausted'
  | 'quota_near_limit';

export interface CatalogueBackfillRun {
  completedAt: string | null;
  durationMs: number | null;
  errorCode: string | null;
  id: string;
  mode: 'dry_run' | 'execute';
  result: {
    ambiguousRows: number;
    enrichedRows: number;
    invalidRows: number;
    noUsefulMetadataRows: number;
    notFoundRows: number;
    requestedQuotaUnits: number;
    staleRows: number;
  };
  scope: 'all_stored' | 'referenced_only';
  selectedIsbns: number;
  selectedRows: number;
  startedAt: string;
  status: RatingRefreshRunStatus;
}

export interface CatalogueProviderOperations {
  ambiguousAttempts: number;
  attempts: number;
  averageDurationMs: number | null;
  availabilityPercent: number | null;
  hitAttempts: number;
  lastAttemptAt: string | null;
  maxDurationMs: number | null;
  notFoundAttempts: number;
  provider: string;
  quotaDeferredItems: number;
  requestItems: number;
  resultItems: number;
  status: CataloguePipelineHealthStatus | 'unknown';
  unavailableAttempts: number;
  unavailableItems: number;
}

export interface CataloguePipelineOperations {
  backfill: {
    inventory: {
      incomplete: number;
      incompleteWithIsbn: number;
      openLibraryTotal: number;
      referencedIncomplete: number;
    };
    lastAttempt: CatalogueBackfillRun | null;
    recentRuns: CatalogueBackfillRun[];
    state: {
      ambiguous: number;
      due: number;
      enriched: number;
      invalid: number;
      noUsefulMetadata: number;
      notFound: number;
      tracked: number;
    };
  };
  enrichment: {
    counts: {
      conflict: number;
      failed: number;
      notFound: number;
      pending: number;
      processing: number;
      succeeded: number;
    };
    duePending: number;
    expiredLeases: number;
    failedLast24Hours: number;
    latestCompletedAt: string | null;
    oldestDueAt: string | null;
  };
  generatedAt: string;
  health: {
    issues: {
      code: CataloguePipelineIssueCode;
      severity: 'critical' | 'warning';
    }[];
    status: CataloguePipelineHealthStatus;
  };
  providers: {
    items: CatalogueProviderOperations[];
    lookbackDays: number;
  };
  quota: {
    dailyLimit: number;
    history: { date: string; used: number }[];
    provider: 'isbndb';
    remainingToday: number;
    usedToday: number;
    utilizationPercent: number;
  };
  watchdog: {
    backfillStuckMinutes: number;
    cron: string;
    enrichmentOverdueMinutes: number;
    timeZone: string;
  };
}

type GetAccessToken = () => Promise<string>;

export const ADMIN_REVIEW_PAGE_LIMIT = 20;

export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
    this.name = 'AdminApiError';
  }
}

export async function syncProfile(
  getAccessToken: GetAccessToken,
  displayName: string,
  providerAvatarUrl: string | null = null,
) {
  await adminRequest(getAccessToken, '/me/profile', {
    body: JSON.stringify({ displayName }),
    method: 'PUT',
  });
  await adminRequest(getAccessToken, '/me/account-presentation', {
    body: JSON.stringify({
      providerAvatarUrl,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    method: 'PUT',
  });
}

export async function assertAdminAccess(getAccessToken: GetAccessToken) {
  await adminRequest(getAccessToken, '/admin/me');
}

export async function getPendingReviews(
  getAccessToken: GetAccessToken,
  filters: AdminReviewFilters,
  cursor: string | null = null,
) {
  const path = pendingReviewsPath(filters, cursor);
  const data = await adminRequest(getAccessToken, path);
  return parsePendingReviewQueue(data);
}

export function pendingReviewsPath(filters: AdminReviewFilters, cursor: string | null = null) {
  return pendingModerationPath('/admin/reviews/pending', filters, cursor);
}

function pendingModerationPath(
  basePath: string,
  filters: AdminReviewFilters,
  cursor: string | null,
) {
  const query = new URLSearchParams({
    limit: String(ADMIN_REVIEW_PAGE_LIMIT),
  });
  if (cursor) {
    query.set('cursor', cursor);
  }
  for (const reviewerId of filters.reviewerIds) {
    query.append('reviewerId', reviewerId);
  }
  if (filters.checkStatus) {
    query.set('checkStatus', filters.checkStatus);
  }
  return `${basePath}?${query.toString()}`;
}

export async function getPendingReviewers(getAccessToken: GetAccessToken) {
  const data = await adminRequest(getAccessToken, '/admin/reviews/pending/reviewers');
  return parsePendingReviewers(data);
}

export async function getPendingQuotes(
  getAccessToken: GetAccessToken,
  filters: AdminReviewFilters,
  cursor: string | null = null,
) {
  const data = await adminRequest(getAccessToken, pendingQuotesPath(filters, cursor));
  return parsePendingQuoteQueue(data);
}

export function pendingQuotesPath(filters: AdminReviewFilters, cursor: string | null = null) {
  return pendingModerationPath('/admin/quotes/pending', filters, cursor);
}

export async function getPendingQuoteReaders(getAccessToken: GetAccessToken) {
  const data = await adminRequest(getAccessToken, '/admin/quotes/pending/readers');
  return parsePendingReviewers(data);
}

export async function getOpenLibraryRatingOperations(getAccessToken: GetAccessToken) {
  const data = await adminRequest(getAccessToken, '/admin/operations/open-library-ratings');
  return parseOpenLibraryRatingOperations(data);
}

export async function getCataloguePipelineOperations(getAccessToken: GetAccessToken) {
  const data = await adminRequest(getAccessToken, '/admin/operations/catalogue-pipeline');
  return parseCataloguePipelineOperations(data);
}

export async function decideReview(
  getAccessToken: GetAccessToken,
  review: Pick<PendingReview, 'id' | 'version'>,
  decision: ReviewDecision,
  moderatorMessage: string | null,
) {
  await adminRequest(getAccessToken, `/admin/reviews/${encodeURIComponent(review.id)}/decision`, {
    body: JSON.stringify({
      ...reviewDecisionPayload(review, decision, moderatorMessage),
    }),
    method: 'POST',
  });
}

export async function decideQuote(
  getAccessToken: GetAccessToken,
  quote: Pick<PendingQuote, 'id' | 'version'>,
  decision: ReviewDecision,
  moderatorMessage: string | null,
) {
  await adminRequest(getAccessToken, `/admin/quotes/${encodeURIComponent(quote.id)}/decision`, {
    body: JSON.stringify(reviewDecisionPayload(quote, decision, moderatorMessage)),
    method: 'POST',
  });
}

export function reviewDecisionPayload(
  review: Pick<PendingReview, 'version'>,
  decision: ReviewDecision,
  moderatorMessage: string | null,
) {
  return {
    decision,
    expectedVersion: review.version,
    ...(decision === 'rejected' ? { message: moderatorMessage } : {}),
  };
}

export async function adminRequest(
  getAccessToken: GetAccessToken,
  path: string,
  init: RequestInit = {},
): Promise<unknown> {
  const token = await getAccessToken();
  const response = await fetch(`${__ADMIN_API_URL__}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new AdminApiError(response.status, 'ADMIN_RESPONSE_INVALID');
  }
  if (!response.ok) {
    throw new AdminApiError(response.status, readErrorCode(data));
  }
  return data;
}

export function parsePendingReviewQueue(value: unknown): PendingReviewQueue {
  const queue = record(value);
  if (
    !Number.isSafeInteger(queue.total) ||
    Number(queue.total) < 0 ||
    !Array.isArray(queue.reviews) ||
    (queue.nextCursor !== undefined &&
      queue.nextCursor !== null &&
      (typeof queue.nextCursor !== 'string' ||
        queue.nextCursor.length < 1 ||
        queue.nextCursor.length > 256))
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    nextCursor: typeof queue.nextCursor === 'string' ? queue.nextCursor : null,
    total: Number(queue.total),
    reviews: queue.reviews.map(parseReview),
  };
}

export function parsePendingQuoteQueue(value: unknown): PendingQuoteQueue {
  const queue = record(value);
  if (
    !Number.isSafeInteger(queue.total) ||
    Number(queue.total) < 0 ||
    !Array.isArray(queue.quotes) ||
    (queue.nextCursor !== undefined &&
      queue.nextCursor !== null &&
      (typeof queue.nextCursor !== 'string' ||
        queue.nextCursor.length < 1 ||
        queue.nextCursor.length > 256))
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    nextCursor: typeof queue.nextCursor === 'string' ? queue.nextCursor : null,
    quotes: queue.quotes.map(parseQuote),
    total: Number(queue.total),
  };
}

export function parsePendingReviewers(value: unknown): PendingReviewerOption[] {
  const response = record(value);
  if (!Array.isArray(response.reviewers)) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return response.reviewers.map((value) => {
    const reviewer = record(value);
    if (
      typeof reviewer.id !== 'string' ||
      (reviewer.name !== null && typeof reviewer.name !== 'string') ||
      !Number.isSafeInteger(reviewer.pendingReviewCount) ||
      Number(reviewer.pendingReviewCount) < 1
    ) {
      throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
    }
    return {
      id: reviewer.id,
      name: reviewer.name as string | null,
      pendingReviewCount: Number(reviewer.pendingReviewCount),
    };
  });
}

export function parseOpenLibraryRatingOperations(value: unknown): OpenLibraryRatingOperations {
  const operations = record(value);
  const coverage = record(operations.coverage);
  const health = record(operations.health);
  const schedule = record(operations.schedule);
  if (
    operations.provider !== 'open_library' ||
    !Array.isArray(operations.recentRuns) ||
    !isHealthStatus(health.status) ||
    !isHealthReason(health.reason) ||
    typeof schedule.cron !== 'string' ||
    typeof schedule.timeZone !== 'string'
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  const recentRuns = operations.recentRuns.map(parseRatingRefreshRun);
  const lastAttempt = parseNullableRatingRefreshRun(operations.lastAttempt);
  const lastSuccess = parseNullableRatingRefreshRun(operations.lastSuccess);
  return {
    coverage: {
      authors: parseRatingCoverage(coverage.authors),
      works: parseRatingCoverage(coverage.works),
    },
    health: {
      reason: health.reason,
      status: health.status,
    },
    lastAttempt,
    lastSuccess,
    provider: 'open_library',
    recentRuns,
    schedule: {
      cron: schedule.cron,
      staleAfterDays: nonNegativeInteger(schedule.staleAfterDays),
      stuckAfterMinutes: nonNegativeInteger(schedule.stuckAfterMinutes),
      timeZone: schedule.timeZone,
    },
  };
}

const catalogueIssueCodes: readonly CataloguePipelineIssueCode[] = [
  'backfill_latest_failed',
  'backfill_stuck',
  'enrichment_failed_jobs',
  'enrichment_lease_expired',
  'enrichment_overdue',
  'provider_unavailable',
  'quota_exhausted',
  'quota_near_limit',
];

export function parseCataloguePipelineOperations(value: unknown): CataloguePipelineOperations {
  const operations = record(value);
  const health = record(operations.health);
  const enrichment = record(operations.enrichment);
  const enrichmentCounts = record(enrichment.counts);
  const providers = record(operations.providers);
  const quota = record(operations.quota);
  const backfill = record(operations.backfill);
  const inventory = record(backfill.inventory);
  const backfillState = record(backfill.state);
  const watchdog = record(operations.watchdog);
  if (
    !isIsoDate(operations.generatedAt) ||
    !isCatalogueHealthStatus(health.status) ||
    !Array.isArray(health.issues) ||
    !Array.isArray(providers.items) ||
    !Array.isArray(quota.history) ||
    quota.provider !== 'isbndb' ||
    !Array.isArray(backfill.recentRuns) ||
    typeof watchdog.cron !== 'string' ||
    typeof watchdog.timeZone !== 'string'
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  const recentRuns = backfill.recentRuns.map(parseCatalogueBackfillRun);
  return {
    backfill: {
      inventory: {
        incomplete: nonNegativeInteger(inventory.incomplete),
        incompleteWithIsbn: nonNegativeInteger(inventory.incompleteWithIsbn),
        openLibraryTotal: nonNegativeInteger(inventory.openLibraryTotal),
        referencedIncomplete: nonNegativeInteger(inventory.referencedIncomplete),
      },
      lastAttempt:
        backfill.lastAttempt === null ? null : parseCatalogueBackfillRun(backfill.lastAttempt),
      recentRuns,
      state: {
        ambiguous: nonNegativeInteger(backfillState.ambiguous),
        due: nonNegativeInteger(backfillState.due),
        enriched: nonNegativeInteger(backfillState.enriched),
        invalid: nonNegativeInteger(backfillState.invalid),
        noUsefulMetadata: nonNegativeInteger(backfillState.noUsefulMetadata),
        notFound: nonNegativeInteger(backfillState.notFound),
        tracked: nonNegativeInteger(backfillState.tracked),
      },
    },
    enrichment: {
      counts: {
        conflict: nonNegativeInteger(enrichmentCounts.conflict),
        failed: nonNegativeInteger(enrichmentCounts.failed),
        notFound: nonNegativeInteger(enrichmentCounts.notFound),
        pending: nonNegativeInteger(enrichmentCounts.pending),
        processing: nonNegativeInteger(enrichmentCounts.processing),
        succeeded: nonNegativeInteger(enrichmentCounts.succeeded),
      },
      duePending: nonNegativeInteger(enrichment.duePending),
      expiredLeases: nonNegativeInteger(enrichment.expiredLeases),
      failedLast24Hours: nonNegativeInteger(enrichment.failedLast24Hours),
      latestCompletedAt: nullableIsoDate(enrichment.latestCompletedAt),
      oldestDueAt: nullableIsoDate(enrichment.oldestDueAt),
    },
    generatedAt: operations.generatedAt,
    health: {
      issues: health.issues.map((value) => {
        const issue = record(value);
        if (
          typeof issue.code !== 'string' ||
          !catalogueIssueCodes.includes(issue.code as CataloguePipelineIssueCode) ||
          (issue.severity !== 'critical' && issue.severity !== 'warning')
        ) {
          throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
        }
        return {
          code: issue.code as CataloguePipelineIssueCode,
          severity: issue.severity,
        };
      }),
      status: health.status,
    },
    providers: {
      items: providers.items.map(parseCatalogueProvider),
      lookbackDays: nonNegativeInteger(providers.lookbackDays),
    },
    quota: {
      dailyLimit: positiveInteger(quota.dailyLimit),
      history: quota.history.map((value) => {
        const day = record(value);
        if (typeof day.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/u.test(day.date)) {
          throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
        }
        return {
          date: day.date,
          used: nonNegativeInteger(day.used),
        };
      }),
      provider: 'isbndb',
      remainingToday: nonNegativeInteger(quota.remainingToday),
      usedToday: nonNegativeInteger(quota.usedToday),
      utilizationPercent: boundedPercent(quota.utilizationPercent),
    },
    watchdog: {
      backfillStuckMinutes: positiveInteger(watchdog.backfillStuckMinutes),
      cron: watchdog.cron,
      enrichmentOverdueMinutes: positiveInteger(watchdog.enrichmentOverdueMinutes),
      timeZone: watchdog.timeZone,
    },
  };
}

function parseCatalogueProvider(value: unknown): CatalogueProviderOperations {
  const provider = record(value);
  if (
    typeof provider.provider !== 'string' ||
    !/^[a-z][a-z0-9_]{0,63}$/u.test(provider.provider) ||
    !isCatalogueProviderStatus(provider.status)
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    ambiguousAttempts: nonNegativeInteger(provider.ambiguousAttempts),
    attempts: nonNegativeInteger(provider.attempts),
    averageDurationMs: nullableNonNegativeNumber(provider.averageDurationMs),
    availabilityPercent:
      provider.availabilityPercent === null ? null : boundedPercent(provider.availabilityPercent),
    hitAttempts: nonNegativeInteger(provider.hitAttempts),
    lastAttemptAt: nullableIsoDate(provider.lastAttemptAt),
    maxDurationMs: nullableNonNegativeNumber(provider.maxDurationMs),
    notFoundAttempts: nonNegativeInteger(provider.notFoundAttempts),
    provider: provider.provider,
    quotaDeferredItems: nonNegativeInteger(provider.quotaDeferredItems),
    requestItems: nonNegativeInteger(provider.requestItems),
    resultItems: nonNegativeInteger(provider.resultItems),
    status: provider.status,
    unavailableAttempts: nonNegativeInteger(provider.unavailableAttempts),
    unavailableItems: nonNegativeInteger(provider.unavailableItems),
  };
}

function parseCatalogueBackfillRun(value: unknown): CatalogueBackfillRun {
  const run = record(value);
  const result = record(run.result);
  if (
    typeof run.id !== 'string' ||
    (run.mode !== 'dry_run' && run.mode !== 'execute') ||
    (run.scope !== 'all_stored' && run.scope !== 'referenced_only') ||
    !isRunStatus(run.status) ||
    !isIsoDate(run.startedAt) ||
    (run.completedAt !== null && !isIsoDate(run.completedAt)) ||
    (run.errorCode !== null && typeof run.errorCode !== 'string')
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    completedAt: run.completedAt as string | null,
    durationMs: nullableNonNegativeInteger(run.durationMs),
    errorCode: run.errorCode as string | null,
    id: run.id,
    mode: run.mode,
    result: {
      ambiguousRows: nonNegativeInteger(result.ambiguousRows),
      enrichedRows: nonNegativeInteger(result.enrichedRows),
      invalidRows: nonNegativeInteger(result.invalidRows),
      noUsefulMetadataRows: nonNegativeInteger(result.noUsefulMetadataRows),
      notFoundRows: nonNegativeInteger(result.notFoundRows),
      requestedQuotaUnits: nonNegativeInteger(result.requestedQuotaUnits),
      staleRows: nonNegativeInteger(result.staleRows),
    },
    scope: run.scope,
    selectedIsbns: nonNegativeInteger(run.selectedIsbns),
    selectedRows: nonNegativeInteger(run.selectedRows),
    startedAt: run.startedAt,
    status: run.status,
  };
}

function parseNullableRatingRefreshRun(value: unknown) {
  return value === null ? null : parseRatingRefreshRun(value);
}

function parseRatingRefreshRun(value: unknown): RatingRefreshRun {
  const run = record(value);
  const candidates = record(run.candidates);
  const result = record(run.result);
  const authors = record(result.authors);
  const works = record(result.works);
  if (
    typeof run.id !== 'string' ||
    (run.source !== 'cloud_run_job' && run.source !== 'operator') ||
    !isRunStatus(run.status) ||
    !isIsoDate(run.startedAt) ||
    !isIsoDate(run.heartbeatAt) ||
    (run.completedAt !== null && !isIsoDate(run.completedAt)) ||
    (run.durationMs !== null &&
      (!Number.isSafeInteger(run.durationMs) || Number(run.durationMs) < 0)) ||
    (run.errorCode !== null && typeof run.errorCode !== 'string')
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    candidates: {
      authors: nonNegativeInteger(candidates.authors),
      works: nonNegativeInteger(candidates.works),
    },
    completedAt: run.completedAt as string | null,
    durationMs: run.durationMs as number | null,
    errorCode: run.errorCode as string | null,
    heartbeatAt: run.heartbeatAt,
    id: run.id,
    result: {
      authors: {
        deferred: nonNegativeInteger(authors.deferred),
        refreshed: nonNegativeInteger(authors.refreshed),
        unresolved: nonNegativeInteger(authors.unresolved),
      },
      works: {
        deferred: nonNegativeInteger(works.deferred),
        refreshed: nonNegativeInteger(works.refreshed),
        unresolved: nonNegativeInteger(works.unresolved),
      },
    },
    source: run.source,
    startedAt: run.startedAt,
    status: run.status,
  };
}

function parseRatingCoverage(value: unknown): RatingCoverage {
  const coverage = record(value);
  return {
    cached: nonNegativeInteger(coverage.cached),
    due: nonNegativeInteger(coverage.due),
    mapped: nonNegativeInteger(coverage.mapped),
    rated: nonNegativeInteger(coverage.rated),
    total: nonNegativeInteger(coverage.total),
    unresolved: nonNegativeInteger(coverage.unresolved),
  };
}

function nonNegativeInteger(value: unknown) {
  if (!Number.isSafeInteger(value) || Number(value) < 0) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return Number(value);
}

function positiveInteger(value: unknown) {
  const parsed = nonNegativeInteger(value);
  if (parsed < 1) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return parsed;
}

function nullableNonNegativeInteger(value: unknown) {
  return value === null ? null : nonNegativeInteger(value);
}

function nullableNonNegativeNumber(value: unknown) {
  if (value === null) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return value;
}

function boundedPercent(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return value;
}

function nullableIsoDate(value: unknown) {
  if (value === null) {
    return null;
  }
  if (!isIsoDate(value)) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return value;
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function isRunStatus(value: unknown): value is RatingRefreshRunStatus {
  return value === 'failed' || value === 'running' || value === 'succeeded';
}

function isCatalogueHealthStatus(value: unknown): value is CataloguePipelineHealthStatus {
  return value === 'critical' || value === 'degraded' || value === 'healthy';
}

function isCatalogueProviderStatus(value: unknown): value is CatalogueProviderOperations['status'] {
  return isCatalogueHealthStatus(value) || value === 'unknown';
}

function isHealthStatus(value: unknown): value is RatingRefreshHealthStatus {
  return (
    value === 'critical' || value === 'healthy' || value === 'never_run' || value === 'running'
  );
}

function isHealthReason(value: unknown): value is OpenLibraryRatingOperations['health']['reason'] {
  return (
    value === 'healthy' ||
    value === 'latest_attempt_failed' ||
    value === 'never_succeeded' ||
    value === 'refresh_running' ||
    value === 'refresh_stuck' ||
    value === 'success_stale'
  );
}

function parseReview(value: unknown): PendingReview {
  const review = record(value);
  const book = record(review.book);
  const contentCheck =
    review.contentCheck === undefined
      ? unavailableContentCheck()
      : parseContentCheck(review.contentCheck);
  const isbn10 = optionalNullableString(book.isbn10);
  const isbn13 = optionalNullableString(book.isbn13);
  const language = optionalNullableString(book.language);
  const pageCount = optionalNullableInteger(book.pageCount, 1);
  const publishedYear = optionalNullableInteger(book.publishedYear);
  const publisher = optionalNullableString(book.publisher);
  const reviewerId = optionalNullableString(review.reviewerId);
  if (
    typeof review.id !== 'string' ||
    typeof review.body !== 'string' ||
    typeof review.containsSpoilers !== 'boolean' ||
    (review.rating !== null && typeof review.rating !== 'number') ||
    (review.reviewerName !== null && typeof review.reviewerName !== 'string') ||
    (review.source !== 'living_library' && review.source !== 'goodreads') ||
    typeof review.submittedAt !== 'string' ||
    !Number.isSafeInteger(review.version) ||
    Number(review.version) < 1 ||
    typeof book.id !== 'string' ||
    typeof book.title !== 'string' ||
    !Array.isArray(book.authors) ||
    !book.authors.every((author) => typeof author === 'string') ||
    (book.coverImageUrl !== null && typeof book.coverImageUrl !== 'string')
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    id: review.id,
    body: review.body,
    book: {
      id: book.id,
      title: book.title,
      authors: book.authors as string[],
      coverImageUrl: book.coverImageUrl as string | null,
      isbn10,
      isbn13,
      language,
      pageCount,
      publishedYear,
      publisher,
    },
    contentCheck,
    containsSpoilers: review.containsSpoilers,
    rating: review.rating as number | null,
    reviewerId,
    reviewerName: review.reviewerName as string | null,
    source: review.source,
    submittedAt: review.submittedAt,
    version: Number(review.version),
  };
}

function parseQuote(value: unknown): PendingQuote {
  const quote = record(value);
  const book = record(quote.book);
  const contentCheck =
    quote.contentCheck === undefined
      ? unavailableContentCheck()
      : parseContentCheck(quote.contentCheck);
  const isbn10 = optionalNullableString(book.isbn10);
  const isbn13 = optionalNullableString(book.isbn13);
  const language = optionalNullableString(book.language);
  const pageCount = optionalNullableInteger(book.pageCount, 1);
  const publishedYear = optionalNullableInteger(book.publishedYear);
  const publisher = optionalNullableString(book.publisher);
  const pageNumber = optionalNullableInteger(quote.pageNumber, 1);
  if (
    typeof quote.id !== 'string' ||
    typeof quote.body !== 'string' ||
    typeof quote.language !== 'string' ||
    typeof quote.readerId !== 'string' ||
    (quote.readerName !== null && typeof quote.readerName !== 'string') ||
    typeof quote.submittedAt !== 'string' ||
    !Number.isSafeInteger(quote.version) ||
    Number(quote.version) < 1 ||
    typeof book.id !== 'string' ||
    typeof book.title !== 'string' ||
    !Array.isArray(book.authors) ||
    !book.authors.every((author) => typeof author === 'string') ||
    (book.coverImageUrl !== null && typeof book.coverImageUrl !== 'string')
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    body: quote.body,
    book: {
      authors: book.authors as string[],
      coverImageUrl: book.coverImageUrl as string | null,
      id: book.id,
      isbn10,
      isbn13,
      language,
      pageCount,
      publishedYear,
      publisher,
      title: book.title,
    },
    contentCheck,
    id: quote.id,
    language: quote.language,
    pageNumber,
    readerId: quote.readerId,
    readerName: quote.readerName as string | null,
    submittedAt: quote.submittedAt,
    version: Number(quote.version),
  };
}

const contentSignalCodes: readonly ReviewContentSignalCode[] = [
  'external_link',
  'promotional_language',
  'contact_solicitation',
  'unsafe_markup',
  'credential_solicitation',
  'obfuscated_text',
];

function unavailableContentCheck(): ReviewContentCheck {
  return {
    method: null,
    signals: [],
    status: 'unavailable',
  };
}

function parseContentCheck(value: unknown): ReviewContentCheck {
  const check = record(value);
  if (
    check.method !== 'heuristic-v1' ||
    (check.status !== 'clear' && check.status !== 'flagged') ||
    !Array.isArray(check.signals)
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  const signals = check.signals.map((value) => {
    const signal = record(value);
    if (
      (signal.category !== 'advertising' && signal.category !== 'malicious') ||
      typeof signal.code !== 'string' ||
      !contentSignalCodes.includes(signal.code as ReviewContentSignalCode)
    ) {
      throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
    }
    return {
      category: signal.category as 'advertising' | 'malicious',
      code: signal.code as ReviewContentSignalCode,
    };
  });
  if (
    (check.status === 'clear' && signals.length !== 0) ||
    (check.status === 'flagged' && signals.length === 0)
  ) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return {
    method: 'heuristic-v1',
    signals,
    status: check.status,
  };
}

function optionalNullableString(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'string') {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return value;
}

function optionalNullableInteger(value: unknown, minimum?: number): number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (!Number.isSafeInteger(value) || (minimum !== undefined && Number(value) < minimum)) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return Number(value);
}

function record(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new AdminApiError(200, 'ADMIN_RESPONSE_INVALID');
  }
  return value as Record<string, unknown>;
}

function readErrorCode(value: unknown) {
  if (
    value &&
    typeof value === 'object' &&
    'message' in value &&
    Array.isArray(value.message) &&
    value.message.every((item) => typeof item === 'string')
  ) {
    return value.message.join('; ');
  }
  if (
    value !== null &&
    typeof value === 'object' &&
    'message' in value &&
    typeof value.message === 'string'
  ) {
    return value.message;
  }
  return 'ADMIN_REQUEST_FAILED';
}
